import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

// The navigation chrome (Sidebar / MobileTabBar) is rendered OUTSIDE the Tabs
// navigator, so it can't reach the Tabs navigation via useNavigation(). The
// Tabs `tabBar` slot, however, IS inside the navigator and receives its
// navigation object + state. We capture both here so the chrome can:
//   1. perform a state-preserving tab jump via dispatch(JUMP_TO) so that
//      re-tapping the already-focused tab is never a no-op — unlike navigate(),
//      which React Navigation ignores when the target tab is already active, and
//   2. read which tab is actually focused for active-item highlighting —
//      authoritative regardless of how the URL/pathname updated.

type TabNavigationAction = { type: 'JUMP_TO'; payload: { name: string } };
type TabNavigator = {
  navigate: (name: string) => void;
  dispatch: (action: TabNavigationAction) => void;
} | null;

// A monotonic "pulse" the chrome emits each time it navigates to a tab (re-tap
// of the active item OR a jump from another section). Screens subscribe to it
// to reset their scroll position to the top — JUMP_TO is state-preserving, so a
// mounted screen otherwise keeps the offset the user left it at (FLOSEAI-416).
interface TabNavPulse {
  tab: string | null;
  seq: number;
}

interface TabsNavigationValue {
  registerTabNavigation: (nav: TabNavigator) => void;
  // Returns true when the jump was handled by the Tabs navigator, false when
  // the tab navigation isn't available yet (caller should fall back to router).
  jumpToTab: (tabRouteName: string) => boolean;
  // Route name of the currently focused tab (e.g. '(leads)', 'dashboard').
  activeTabRouteName: string | null;
  reportActiveTab: (tabRouteName: string) => void;
  // Latest chrome navigation pulse; bumped by signalTabNavigation.
  navPulse: TabNavPulse;
  signalTabNavigation: (tabRouteName: string) => void;
}

const TabsNavigationContext = createContext<TabsNavigationValue | null>(null);

export function TabsNavigationProvider({ children }: Readonly<{ children: ReactNode }>) {
  const navRef = useRef<TabNavigator>(null);
  const [activeTabRouteName, setActiveTabRouteName] = useState<string | null>(null);
  const [navPulse, setNavPulse] = useState<TabNavPulse>({ tab: null, seq: 0 });

  const registerTabNavigation = useCallback((nav: TabNavigator) => {
    navRef.current = nav;
  }, []);

  const jumpToTab = useCallback((tabRouteName: string) => {
    if (!navRef.current) return false;
    navRef.current.dispatch({ type: 'JUMP_TO', payload: { name: tabRouteName } });
    return true;
  }, []);

  const reportActiveTab = useCallback((tabRouteName: string) => {
    setActiveTabRouteName((prev) => (prev === tabRouteName ? prev : tabRouteName));
  }, []);

  const signalTabNavigation = useCallback((tabRouteName: string) => {
    setNavPulse((prev) => ({ tab: tabRouteName, seq: prev.seq + 1 }));
  }, []);

  const value = useMemo(
    () => ({
      registerTabNavigation,
      jumpToTab,
      activeTabRouteName,
      reportActiveTab,
      navPulse,
      signalTabNavigation,
    }),
    [
      registerTabNavigation,
      jumpToTab,
      activeTabRouteName,
      reportActiveTab,
      navPulse,
      signalTabNavigation,
    ],
  );

  return <TabsNavigationContext.Provider value={value}>{children}</TabsNavigationContext.Provider>;
}

export function useTabsNavigation(): TabsNavigationValue | null {
  return useContext(TabsNavigationContext);
}
